import type { SessionLifecycleLoadResult } from '@core/entities/SessionLifecycle';
import type { SessionLifecycleGateway } from '@core/ports/SessionLifecycleGateway';
import { AuthenticatedBffTransport } from '@infrastructure/http/AuthenticatedBffTransport';

export class BffSessionLifecycleHttpGateway implements SessionLifecycleGateway {
  public constructor(private readonly transport = new AuthenticatedBffTransport()) {}

  public async extend(): Promise<SessionLifecycleLoadResult> {
    return this.load('/api/auth/session/extend', {
      method: 'POST',
      headers: {
        Accept: 'application/json'
      }
    });
  }

  public async getStatus(): Promise<SessionLifecycleLoadResult> {
    return this.load('/api/auth/session/lifecycle', {
      headers: {
        Accept: 'application/json'
      }
    });
  }

  public async logOut(): Promise<void> {
    const response = await this.transport.fetch('/api/auth/session/logout', {
      method: 'POST',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Session logout failed with status ${response.status}.`);
    }
  }

  private async load(input: string, init: RequestInit): Promise<SessionLifecycleLoadResult> {
    try {
      const response = await this.transport.fetch(input, init);

      if (response.status === 401) {
        return {
          kind: 'session-required',
          message: 'Start this journey from the NHS App to continue.'
        };
      }

      if (!response.ok) {
        return {
          kind: 'session-unavailable',
          message: 'Your session cannot be confirmed right now. Start again from the NHS App to continue.'
        };
      }

      return {
        kind: 'available',
        status: (await response.json())
      };
    } catch {
      return {
        kind: 'session-unavailable',
        message: 'Your session cannot be confirmed right now. Start again from the NHS App to continue.'
      };
    }
  }
}
